import type { ApiLot } from '@/types/inventory'

/**
 * Receipts of a vendor lot this material already holds.
 *
 * Receiving the same supplier lot again does not open a new lot — the server
 * reuses that lot's base and gives the receipt the next suffix, so
 * `LOT-26-0004-1` is followed by `-2`. That is the behaviour we want and it is
 * invisible at the point of entry, which is why the dialog asks first.
 *
 * The rule here MIRRORS the server's and must not drift from it. On receipt the
 * server stores `vendorLotNumber?.trim() || null` and probes the family with
 * `where: { tenantId, materialId, vendorLotNumber }` — plain Postgres equality.
 * So: trimmed, case-SENSITIVE, and already scoped to one material by the lots
 * passed in. Matching `a77` to `A77` would tell the operator their receipt is
 * joining a family when the server would in fact open a new one, and a
 * confirmation that misstates what is about to happen is worse than none.
 *
 * A blank matches nothing. Re-labelled and self-produced stock carries no
 * supplier number, and one absence is not a value two receipts can share.
 */
export function matchingVendorLots(
  lots: ApiLot[] | undefined,
  vendorLotNumber: string,
): ApiLot[] {
  const wanted = vendorLotNumber.trim()
  if (!wanted) return []
  return (lots ?? []).filter((l) => (l.vendorLotNumber ?? '') === wanted)
}
